// UsersPage — C-USERS, view-only.
//
// Ported from the 2026-08-26 design drop,
// handoff/commerce-app/src/pages/UsersPage.tsx. The page header, the card, the
// table and the copy's voice are the handoff's. What is gone is gone because it
// describes a service this is not, and each removal is one decision:
//
// ADAPTED — no Role column, and no role-descriptions grid. `CustomerMembership`
// has no role field. The handoff's three tiers — Billing Admin, Admin, Viewer,
// each with a sentence about what it may do — describe an authorisation model
// nothing in this service enforces. A column showing "Billing Admin" for
// everybody would not be a placeholder; it would be a claim about permissions,
// and it would be false. So the table shows the three facts this service holds:
// who they are, how to reach them, and when they joined (Owner D7).
//
// ADAPTED — no "Invite user" button and no invite state. There is no invite
// flow and no email infrastructure behind one; a membership is created by
// somebody claiming a subscription. The handoff's Status column went with it,
// because "active" versus "invited" is a distinction only an invite flow makes.
//
// ADAPTED — no "Connect SSO" button and no SSO callout. This service does have
// one federated route — allauth's OpenID Connect, configured from the
// environment — but it is a deployment-time setting, not something a customer
// switches on from a page. A button that led nowhere would be worse than none.
//
// ADAPTED — no "Last active" column. Nothing records it. Django's
// `last_login` is close but it is not last activity, and labelling one as the
// other is the kind of small lie a customer eventually catches.
import { useCallback } from 'react';
import { Users } from 'lucide-react';
import { Badge } from '@miq/design-system/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@miq/design-system/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@miq/design-system/components/ui/table';

import { onDay } from '../data/instants';
import { usePortalSession } from '../auth/context';
import { fetchPeople } from '../data/subscriptions';
import { useLoaded } from '../data/useLoaded';
import { useDocumentTitle } from '../../lib/documentTitle';

export function UsersPage() {
  useDocumentTitle('Users');
  const { authorizedFetch } = usePortalSession();
  const read = useCallback(() => fetchPeople(authorizedFetch), [authorizedFetch]);
  const { state } = useLoaded(read);

  return (
    <div className="space-y-5 p-6">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Commerce · Users
        </div>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">Users</h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          Who can sign in to this Commerce portal for your company. Access inside
          your mDecide deployments is managed per app, not here.
        </p>
      </div>

      {/* ADAPTED: the handoff's SSO strip becomes the one honest note this page
          has to make — how somebody joins this list, since no button here can
          add them. */}
      <div className="flex items-center gap-2.5 rounded-lg border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
        <Users className="size-4 shrink-0" />
        <span className="flex-1">
          Everyone here signs in with an email address and a password. A person
          joins this list by claiming a subscription for your company — ask us if
          you need somebody added or removed.
        </span>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">People</CardTitle>
          <CardDescription>
            {state.status === 'loaded'
              ? `${state.data.people.length} · ${state.data.customer.display_name}`
              : 'Reading…'}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {state.status === 'loading' && (
            <p className="px-6 py-8 text-sm text-muted-foreground">
              Reading who signs in for your company…
            </p>
          )}
          {state.status === 'refused' && (
            <p role="alert" className="px-6 py-8 text-sm text-destructive">
              {state.refusal}
            </p>
          )}
          {state.status === 'loaded' && state.data.people.length === 0 && (
            <p className="px-6 py-8 text-sm text-muted-foreground">
              Nobody yet — which should not be possible while you are reading
              this, so please tell us if you see it.
            </p>
          )}
          {state.status === 'loaded' && state.data.people.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  {/* No sign-in column: GET /portal/api/people/ reports email,
                      name, joined and is_you and nothing about how anybody
                      signs in, while this service has a federated door as well
                      as a password one. The column said "Email and password"
                      on every row regardless. */}
                  <TableHead>Joined</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {state.data.people.map((person) => (
                  <TableRow key={person.email}>
                    <TableCell>
                      <div className="flex items-center gap-2 font-medium">
                        {/* Where somebody gave no name, the address is what is
                            real — not a placeholder made from it. */}
                        {person.name || person.email}
                        {person.is_you && <Badge variant="secondary">You</Badge>}
                      </div>
                      {person.name && (
                        <div className="text-xs text-muted-foreground">
                          {person.email}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {onDay(person.joined)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
